import * as R from 'ramda';

import { WeeklyScheduleInputEntry } from './types';
import { WeeklySchedule } from '../types';
import { DAYS_OF_WEEK } from '../values';

// WeeklyTimePair is an intermediate data structure that the algorithm
// primarily works in. It represents a weekly reoccurring time span.
export type WeeklyTimePair = {
  // Values are 0 <= value < 7 * 24 * 60 * 60 * 1000
  start: number;
  end: number;
};

const MINUTE_DURATION_MS = 60 * 1000;
const DAY_DURATION_MS = 24 * 60 * MINUTE_DURATION_MS;
const WEEK_DURATION_MS = 7 * DAY_DURATION_MS;

const timeIsWithinTimePair = (timePair: WeeklyTimePair, timeFromStartOfWeek: number) => {
  const { start } = timePair;
  // Handle case where time pair overlaps Sunday-Monday midnight
  const end = timePair.end <= start ? timePair.end + WEEK_DURATION_MS : timePair.end;
  // Handle case where time pair end overlaps Sunday-Monday midnight and
  // timeFromStartOfWeek is between Sunday-Monday midnight and time pair end.
  const time = timeFromStartOfWeek < start ? timeFromStartOfWeek + WEEK_DURATION_MS : timeFromStartOfWeek;
  return time >= start && time < end;
};

const timePairsOverlap = (timePairA: WeeklyTimePair, timePairB: WeeklyTimePair) => {
  return timeIsWithinTimePair(timePairA, timePairB.start) || timeIsWithinTimePair(timePairB, timePairA.start);
};

const timePairsAreContiguous = (timePairA: WeeklyTimePair, timePairB: WeeklyTimePair) => {
  return timePairA.start === timePairB.end || timePairB.start === timePairA.end;
};

const findIndicesOfConnectedTimePairs = (timePairs: WeeklyTimePair[]) => {
  for (let i = 0; i < timePairs.length; i += 1) {
    for (let j = i + 1; j < timePairs.length; j += 1) {
      if (timePairsOverlap(timePairs[i], timePairs[j]) || timePairsAreContiguous(timePairs[i], timePairs[j])) {
        return [i, j];
      }
    }
  }
  return undefined;
};

// Order `times` starting from the tirst item that comes after `referenceTime`
const orderWeeklyTimes = (referenceTime: number, times: number[]) => {
  const wrapAfterReferenceTime = (time: number) => (time <= referenceTime ? time + WEEK_DURATION_MS : time);
  return times.sort((a, b) => wrapAfterReferenceTime(a) - wrapAfterReferenceTime(b));
};

const timePairDuration = (timePair: WeeklyTimePair) => {
  return timePair.start < timePair.end
    ? timePair.end - timePair.start
    : timePair.end + WEEK_DURATION_MS - timePair.start;
};

const mergeWeeklyTimePairs = (timePairA: WeeklyTimePair, timePairB: WeeklyTimePair): WeeklyTimePair => {
  if (timePairsOverlap(timePairA, timePairB)) {
    const start = timeIsWithinTimePair(timePairA, timePairB.start) ? timePairA.start : timePairB.start;
    return {
      start,
      end: orderWeeklyTimes(start, [timePairA.end, timePairB.end])[1],
    };
  }

  if (timePairsAreContiguous(timePairA, timePairB)) {
    return timePairA.end === timePairB.start
      ? { start: timePairA.start, end: timePairB.end }
      : { start: timePairB.start, end: timePairA.end };
  }

  throw new Error('Time pairs are not connected');
};

export const entriesToWeeklyTimePairs = (weeklyScheduleEntries: WeeklyScheduleInputEntry[]): WeeklyTimePair[] => {
  type NormalizedEntry = {
    weekdayIndices: number[];
    timePairs: { start: number; end: number }[];
  };

  const entriesToNormalizedEntries = (entries: WeeklyScheduleInputEntry[]): NormalizedEntry[] => {
    return entries.map(entry => {
      return {
        weekdayIndices: entry.daysOfWeek.map(weekday => DAYS_OF_WEEK.indexOf(weekday)),
        timePairs: entry.open24Hours
          ? [{ start: 0, end: DAY_DURATION_MS }]
          : entry.timePairs.map(timePair => ({
              start: timePair.startMinutes * MINUTE_DURATION_MS,
              end: timePair.endMinutes * MINUTE_DURATION_MS,
            })),
      };
    });
  };

  const normalizedEntriesToWeeklyTimePairs = (entries: NormalizedEntry[]) =>
    entries.flatMap(rawEntry => {
      return rawEntry.weekdayIndices.flatMap(weekdayIndex => {
        return rawEntry.timePairs.map(timePair => {
          return {
            start: (timePair.start + weekdayIndex * DAY_DURATION_MS) % WEEK_DURATION_MS,
            end: (timePair.end + weekdayIndex * DAY_DURATION_MS) % WEEK_DURATION_MS,
          };
        });
      });
    });

  return normalizedEntriesToWeeklyTimePairs(entriesToNormalizedEntries(weeklyScheduleEntries));
};

export const mergeConnectedTimePairs = (timePairs: WeeklyTimePair[]): WeeklyTimePair[] => {
  const indicesOfConnectedTimePairs = findIndicesOfConnectedTimePairs(timePairs);

  if (!indicesOfConnectedTimePairs) {
    return timePairs;
  }

  const [timePairAIndex, timePairBIndex] = indicesOfConnectedTimePairs;

  const timePairA = timePairs[timePairAIndex];
  const timePairB = timePairs[timePairBIndex];

  return mergeConnectedTimePairs(
    R.remove(timePairBIndex, 1, R.update(timePairAIndex, mergeWeeklyTimePairs(timePairA, timePairB), timePairs)),
  );
};

export const normalizeWeekLongTimePairs = (timePairs: WeeklyTimePair[]): WeeklyTimePair[] => {
  return timePairs.map(timePair => (timePairDuration(timePair) === WEEK_DURATION_MS ? { start: 0, end: 0 } : timePair));
};

export const weeklyTimePairsToOpeningTimes = (weeklyTimePairs: WeeklyTimePair[]): WeeklySchedule => {
  type FlatEvent = {
    type: 'open' | 'close';
    value: number;
    weekdayIndex: number;
  };

  const weeklyTimePairsToFlatEvents = (timePairs: WeeklyTimePair[]): FlatEvent[] => {
    return timePairs.flatMap(timePair => {
      const closeEvent: FlatEvent = {
        type: 'close',
        value: timePair.end % DAY_DURATION_MS,
        weekdayIndex: Math.floor(timePair.end / DAY_DURATION_MS),
      };
      return [
        {
          type: 'open',
          value: timePair.start % DAY_DURATION_MS,
          weekdayIndex: Math.floor(timePair.start / DAY_DURATION_MS),
        },
        closeEvent.value === 0
          ? {
              ...closeEvent,
              // Restaurant API expects close event value to always be greater than 0,
              // so move the event to the previous weekday if its value is 0.
              value: closeEvent.value + DAY_DURATION_MS,
              weekdayIndex: (closeEvent.weekdayIndex + 6) % 7,
            }
          : closeEvent,
      ];
    });
  };

  const flatEventsToOpeningTimes = (flatEvents: FlatEvent[]): WeeklySchedule =>
    flatEvents.reduce(
      (openingTimes, flatEvent) => {
        const weekday = DAYS_OF_WEEK[flatEvent.weekdayIndex];
        return {
          ...openingTimes,
          [weekday]: [...openingTimes[weekday], { type: flatEvent.type, value: { $date: flatEvent.value } }]
            // Restaurant API expects events to be ordered by time
            .sort((a, b) => a.value.$date - b.value.$date),
        };
      },
      {
        monday: [],
        tuesday: [],
        wednesday: [],
        thursday: [],
        friday: [],
        saturday: [],
        sunday: [],
      },
    );

  return flatEventsToOpeningTimes(weeklyTimePairsToFlatEvents(weeklyTimePairs));
};

const fromWeeklyScheduleInputEntriesToWeeklySchedule: (entries: WeeklyScheduleInputEntry[]) => WeeklySchedule = R.pipe(
  entriesToWeeklyTimePairs,
  mergeConnectedTimePairs,
  // If there is a time pair that covers the whole week, make it always start on Monday at 00:00 and end on Sunday at
  // 24:00. Restaurant API does not allow a time span to have the same start and end time, so a time span covering the
  // whole week always has to start and end at midnight. So eg. a time span starting on Wednesday 12:00 and ending on
  // Wednesday 12:00 is not allowed while a time span starting on Wednesday 00:00 and ending on Tuesday 24:00 is ok.
  normalizeWeekLongTimePairs,
  weeklyTimePairsToOpeningTimes,
);

export default fromWeeklyScheduleInputEntriesToWeeklySchedule;
